import { useNavigate } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { Calendar, MapPin, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { SearchLocationMode } from "@/api/types";
import type { RecentSearch, SearchBarState } from "@/features/search/types";
import { cn } from "@/lib/cn";
import { isSameDay, parseISODate } from "@/lib/dates";
import { canAnimateSearchTransition } from "@/lib/search-transition";
import { toggleCategorySelection } from "./category-selection";
import {
  formatSearchBarLocationLabel,
  useMapAutoSearchPref,
} from "./format-search-bar-location-label";
import { formatTreatmentLabel } from "./format-treatment-label";
import { MobileSearchEntry } from "./mobile-search-entry";
import { saveRecentSearch } from "./recent-searches";
import { recentToSearchParams } from "./search-params";
import { TreatmentPanel } from "./treatment-panel";
import { useSearchBarInteraction } from "./use-search-bar-interaction";
import { WhenPanel } from "./when-panel";
import { WherePanel } from "./where-panel";

interface SearchBarProps {
  mode: "expanded" | "compact";
  initialParams?: Partial<SearchBarState>;
  large?: boolean;
}

const DEFAULT_STATE: SearchBarState = {
  query: "",
  category: null,
  categoryName: null,
  location: null,
  lat: null,
  lng: null,
  bounds: null,
  locationMode: null,
  whenDate: null,
  whenTime: null,
};

// fallow-ignore-next-line complexity
function stateToRecentSearch(state: SearchBarState): RecentSearch {
  return {
    q: state.query || undefined,
    category: state.category ?? undefined,
    categoryName: state.categoryName ?? undefined,
    location: state.location ?? undefined,
    lat: state.lat ?? undefined,
    lng: state.lng ?? undefined,
    bounds: state.bounds ?? undefined,
    location_mode: state.locationMode ?? undefined,
    when_date: state.whenDate ?? undefined,
    when_time: state.whenTime ?? undefined,
  };
}

function parseLocationMode(value: unknown): SearchLocationMode | undefined {
  return value === "place" || value === "viewport" || value === "coords" ? value : undefined;
}

function paramsToRecentSearch(params: Record<string, string | number>): RecentSearch {
  return {
    q: typeof params.q === "string" ? params.q : undefined,
    category: typeof params.category === "string" ? params.category : undefined,
    categoryName: typeof params.category_name === "string" ? params.category_name : undefined,
    location: typeof params.location === "string" ? params.location : undefined,
    lat: typeof params.lat === "number" ? params.lat : undefined,
    lng: typeof params.lng === "number" ? params.lng : undefined,
    bounds: typeof params.bounds === "string" ? params.bounds : undefined,
    location_mode: parseLocationMode(params.location_mode),
    when_date: typeof params.when_date === "string" ? params.when_date : undefined,
    when_time: typeof params.when_time === "string" ? params.when_time : undefined,
  };
}

// fallow-ignore-next-line complexity
function SearchBar({ mode, initialParams, large }: SearchBarProps) {
  const [state, setState] = useState<SearchBarState>({
    ...DEFAULT_STATE,
    ...initialParams,
  });
  const navigate = useNavigate();

  // fallow-ignore-next-line complexity
  useEffect(() => {
    setState({
      query: initialParams?.query ?? DEFAULT_STATE.query,
      category: initialParams?.category ?? DEFAULT_STATE.category,
      categoryName: initialParams?.categoryName ?? DEFAULT_STATE.categoryName,
      location: initialParams?.location ?? DEFAULT_STATE.location,
      lat: initialParams?.lat ?? DEFAULT_STATE.lat,
      lng: initialParams?.lng ?? DEFAULT_STATE.lng,
      bounds: initialParams?.bounds ?? DEFAULT_STATE.bounds,
      locationMode: initialParams?.locationMode ?? DEFAULT_STATE.locationMode,
      whenDate: initialParams?.whenDate ?? DEFAULT_STATE.whenDate,
      whenTime: initialParams?.whenTime ?? DEFAULT_STATE.whenTime,
    });
  }, [
    initialParams?.query,
    initialParams?.category,
    initialParams?.categoryName,
    initialParams?.location,
    initialParams?.lat,
    initialParams?.lng,
    initialParams?.bounds,
    initialParams?.locationMode,
    initialParams?.whenDate,
    initialParams?.whenTime,
  ]);

  const handleSubmit = useCallback(
    (fromPointer = false) => {
      const recent = stateToRecentSearch(state);
      saveRecentSearch(recent);
      void navigate({
        to: "/search",
        search: recentToSearchParams(recent),
        viewTransition: mode === "expanded" && fromPointer && canAnimateSearchTransition(),
      });
    },
    [state, navigate, mode],
  );

  const handleRecentSelect = useCallback(
    (search: RecentSearch) => {
      setState({
        query: search.q ?? "",
        category: search.category ?? null,
        categoryName: search.categoryName ?? null,
        location: search.location ?? null,
        lat: search.lat ?? null,
        lng: search.lng ?? null,
        bounds: search.bounds ?? null,
        locationMode: search.location_mode ?? null,
        whenDate: search.when_date ?? null,
        whenTime: (search.when_time as SearchBarState["whenTime"]) ?? null,
      });
      saveRecentSearch(search);
      void navigate({ to: "/search", search: recentToSearchParams(search) });
    },
    [navigate],
  );

  if (mode === "expanded") {
    return (
      <ExpandedSearchBar
        state={state}
        setState={setState}
        onSubmit={handleSubmit}
        onRecentSelect={handleRecentSelect}
        large={large}
      />
    );
  }

  return (
    <CompactSearchBar
      state={state}
      setState={setState}
      onSubmit={handleSubmit}
      onRecentSelect={handleRecentSelect}
    />
  );
}

interface BarWithRecentsProps {
  state: SearchBarState;
  setState: React.Dispatch<React.SetStateAction<SearchBarState>>;
  onSubmit: (fromPointer?: boolean) => void;
  onRecentSelect: (search: RecentSearch) => void;
  large?: boolean;
}

// fallow-ignore-next-line complexity
function ExpandedSearchBar({
  state,
  setState,
  onSubmit,
  onRecentSelect,
  large,
}: BarWithRecentsProps) {
  const {
    isMobile,
    activeField,
    setActiveField,
    containerRef,
    handleFieldClick,
    handleSearchSubmit,
    advanceField,
  } = useSearchBarInteraction(onSubmit);

  const navigate = useNavigate();
  const [entryOpen, setEntryOpen] = useState(false);
  const mapAutoSearch = useMapAutoSearchPref();

  const treatmentLabel = formatTreatmentLabel(state);
  const locationLabel = formatSearchBarLocationLabel(state.location, mapAutoSearch);
  const whenLabel = formatWhenLabel(state.whenDate, state.whenTime);

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          className={cn(
            "flex h-14 w-full items-center justify-center gap-2 rounded-pill bg-electric-700 px-5 text-[16px] font-semibold text-white transition-colors hover:bg-electric-800 active:bg-electric-800 focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-2",
            !entryOpen && "search-route-bar",
          )}
          onClick={() => setEntryOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={entryOpen}
        >
          <Search className="size-5 shrink-0" />
          Search treatments or salons
        </button>
        {createPortal(
          <AnimatePresence>
            {entryOpen && (
              <MobileSearchEntry
                initialState={state}
                onClose={() => setEntryOpen(false)}
                onSearch={(params, fromPointer = false) => {
                  saveRecentSearch(paramsToRecentSearch(params));
                  void navigate({
                    to: "/search",
                    search: params,
                    viewTransition: fromPointer && canAnimateSearchTransition(),
                  });
                }}
              />
            )}
          </AnimatePresence>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={cn(
          "search-route-bar flex items-stretch border bg-surface transition-[box-shadow,border-color] duration-short ease-default",
          large ? "h-16" : "h-14",
          activeField
            ? `rounded-b-none border-fog-dark shadow-soft2 ${large ? "rounded-t-[32px]" : "rounded-t-[28px]"}`
            : "rounded-pill border-fog shadow-soft1 hover:shadow-soft2",
        )}
      >
        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 rounded-l-[inherit] transition-colors",
            large ? "px-6 text-base" : "px-5 text-[15px]",
            activeField === "treatment" ? "bg-fog/60" : "hover:bg-fog/40",
          )}
          onClick={() => handleFieldClick("treatment")}
        >
          <Search className={cn("shrink-0 text-slate", large ? "size-5" : "size-4")} />
          <span className="truncate text-ink">{treatmentLabel}</span>
        </button>

        <span className="my-3 w-px shrink-0 bg-fog-dark" />

        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 transition-colors",
            large ? "px-6 text-base" : "px-5 text-[15px]",
            activeField === "location" ? "bg-fog/60" : "hover:bg-fog/40",
          )}
          onClick={() => handleFieldClick("location")}
        >
          <MapPin className={cn("shrink-0 text-slate", large ? "size-5" : "size-4")} />
          <span className="truncate text-ink">{locationLabel}</span>
        </button>

        <span className="my-3 w-px shrink-0 bg-fog-dark" />

        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 transition-colors",
            large ? "px-6 text-base" : "px-5 text-[15px]",
            activeField === "when" ? "bg-fog/60" : "hover:bg-fog/40",
          )}
          onClick={() => handleFieldClick("when")}
        >
          <Calendar className={cn("shrink-0 text-slate", large ? "size-5" : "size-4")} />
          <span className="truncate text-ink">{whenLabel}</span>
        </button>

        <div className={cn("flex shrink-0 items-center", large ? "pr-2" : "pr-1.5")}>
          <button
            type="button"
            className={cn(
              "flex items-center rounded-pill bg-electric-700 font-semibold text-white transition-colors hover:bg-electric-800 focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-2",
              large ? "h-12 px-8 text-[15px]" : "h-10 px-6 text-[14px]",
            )}
            onClick={handleSearchSubmit}
          >
            Search
          </button>
        </div>
      </div>

      {activeField && (
        <div
          className={cn(
            "animate-search-dropdown absolute left-0 right-0 top-full z-50 border border-t-0 border-fog-dark bg-surface shadow-soft2",
            large ? "rounded-b-[32px]" : "rounded-b-[28px]",
          )}
        >
          <div key={activeField} className="animate-search-panel">
            {activeField === "treatment" && (
              <TreatmentPanel
                query={state.query}
                category={state.category}
                onQueryChange={(q) => setState((s) => ({ ...s, query: q }))}
                onCategoryChange={(key, name) => {
                  setState((s) => {
                    if (!key || !name) {
                      return { ...s, category: null, categoryName: null };
                    }
                    return {
                      ...s,
                      ...toggleCategorySelection(s.category, s.categoryName, key, name),
                    };
                  });
                }}
                onRecentSelect={(search) => {
                  onRecentSelect(search);
                  setActiveField(null);
                }}
                onSubmit={handleSearchSubmit}
              />
            )}
            {activeField === "location" && (
              <WherePanel
                value={state.location}
                onChange={(loc, lat, lng, bounds, locationMode) => {
                  setState((s) => ({ ...s, location: loc, lat, lng, bounds, locationMode }));
                  advanceField("when");
                }}
              />
            )}
            {activeField === "when" && (
              <WhenPanel
                date={state.whenDate}
                time={state.whenTime}
                onDateChange={(d) => setState((s) => ({ ...s, whenDate: d }))}
                onTimeChange={(t) => setState((s) => ({ ...s, whenTime: t }))}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// fallow-ignore-next-line complexity
function CompactSearchBar({ state, setState, onSubmit, onRecentSelect }: BarWithRecentsProps) {
  const {
    isMobile,
    activeField,
    setActiveField,
    containerRef,
    handleFieldClick,
    handleSearchSubmit,
    advanceField,
  } = useSearchBarInteraction(onSubmit);

  const navigate = useNavigate();
  const [entryOpen, setEntryOpen] = useState(false);
  const mapAutoSearch = useMapAutoSearchPref();

  const treatmentLabel = formatTreatmentLabel(state);
  const locationLabel = formatSearchBarLocationLabel(state.location, mapAutoSearch);
  const whenLabel = formatWhenLabel(state.whenDate, state.whenTime);

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          className={cn(
            "flex h-10 w-full items-center gap-2 rounded-pill border border-fog bg-surface px-4 text-[14px] shadow-flat transition-shadow hover:shadow-soft1",
            !entryOpen && "search-route-bar",
          )}
          onClick={() => setEntryOpen(true)}
        >
          <Search className="size-4 shrink-0 text-slate" />
          <span className="min-w-0 flex-1 truncate text-left text-ink">
            {treatmentLabel} · {locationLabel}
          </span>
          {whenLabel !== "Any time" && (
            <span className="shrink-0 text-[12px] text-slate">{` · ${whenLabel}`}</span>
          )}
        </button>
        {createPortal(
          <AnimatePresence>
            {entryOpen && (
              <MobileSearchEntry
                initialState={state}
                onClose={() => setEntryOpen(false)}
                onSearch={(params) => {
                  setEntryOpen(false);
                  saveRecentSearch(paramsToRecentSearch(params));
                  void navigate({ to: "/search", search: params });
                }}
              />
            )}
          </AnimatePresence>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={cn(
          "search-route-bar flex h-12 items-stretch border bg-surface transition-[box-shadow,border-color] duration-short ease-default",
          activeField
            ? "rounded-t-[24px] rounded-b-none border-fog-dark shadow-soft2"
            : "rounded-pill border-fog shadow-flat hover:shadow-soft1",
        )}
      >
        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 rounded-l-[inherit] px-4 text-[14px] transition-colors",
            activeField === "treatment" ? "bg-fog/60" : "hover:bg-fog/40",
          )}
          onClick={() => handleFieldClick("treatment")}
        >
          <Search className="size-4 shrink-0 text-slate" />
          <span className="truncate text-ink">{treatmentLabel}</span>
        </button>

        <span className="my-2.5 w-px shrink-0 bg-fog-dark" />

        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 px-4 text-[14px] transition-colors",
            activeField === "location" ? "bg-fog/60" : "hover:bg-fog/40",
          )}
          onClick={() => handleFieldClick("location")}
        >
          <MapPin className="size-4 shrink-0 text-slate" />
          <span className="truncate text-ink">{locationLabel}</span>
        </button>

        <span className="my-2.5 w-px shrink-0 bg-fog-dark" />

        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 px-4 text-[14px] transition-colors",
            activeField === "when" ? "bg-fog/60" : "hover:bg-fog/40",
          )}
          onClick={() => handleFieldClick("when")}
        >
          <Calendar className="size-4 shrink-0 text-slate" />
          <span className="truncate text-ink">{whenLabel}</span>
        </button>

        <div className="flex shrink-0 items-center pr-1.5">
          <button
            type="button"
            className="flex size-9 items-center justify-center rounded-full bg-electric-700 text-white transition-colors hover:bg-electric-800 focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-2"
            onClick={handleSearchSubmit}
            aria-label="Search"
          >
            <Search className="size-4" />
          </button>
        </div>
      </div>

      {activeField && (
        <div className="animate-search-dropdown absolute left-0 right-0 top-full z-50 rounded-b-[24px] border border-t-0 border-fog-dark bg-surface shadow-soft2">
          <div key={activeField} className="animate-search-panel">
            {activeField === "treatment" && (
              <TreatmentPanel
                query={state.query}
                category={state.category}
                onQueryChange={(q) => setState((s) => ({ ...s, query: q }))}
                onCategoryChange={(key, name) => {
                  setState((s) => {
                    if (!key || !name) {
                      return { ...s, category: null, categoryName: null };
                    }
                    return {
                      ...s,
                      ...toggleCategorySelection(s.category, s.categoryName, key, name),
                    };
                  });
                }}
                onRecentSelect={(search) => {
                  onRecentSelect(search);
                  setActiveField(null);
                }}
                onSubmit={handleSearchSubmit}
              />
            )}
            {activeField === "location" && (
              <WherePanel
                value={state.location}
                onChange={(loc, lat, lng, bounds, locationMode) => {
                  setState((s) => ({ ...s, location: loc, lat, lng, bounds, locationMode }));
                  advanceField("when");
                }}
              />
            )}
            {activeField === "when" && (
              <WhenPanel
                date={state.whenDate}
                time={state.whenTime}
                onDateChange={(d) => setState((s) => ({ ...s, whenDate: d }))}
                onTimeChange={(t) => setState((s) => ({ ...s, whenTime: t }))}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// fallow-ignore-next-line complexity
function formatWhenLabel(date: string | null, time: string | null): string {
  if (!date && (!time || time === "any")) return "Any time";
  const parts: string[] = [];
  if (date) {
    const d = parseISODate(date);
    if (!d) return "Choose a date";
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    if (isSameDay(d, today)) parts.push("Today");
    else if (isSameDay(d, tomorrow)) parts.push("Tomorrow");
    else {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      parts.push(`${months[d.getMonth()]} ${d.getDate()}`);
    }
  }
  if (time && time !== "any") {
    const labels: Record<string, string> = {
      morning: "Morning",
      afternoon: "Afternoon",
      evening: "Evening",
    };
    parts.push(labels[time] ?? time);
  }
  return parts.join(", ") || "Any time";
}

export { SearchBar, type SearchBarProps };
